import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { without } from 'lodash';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';

import { useIsCurrentUserAdmin } from 'src/store';
import { getChannelPerformanceLabel, getChannelPerformanceShortLabel } from 'src/data/channel';
import ChannelIcon from 'src/components/ChannelIcon';
import TextLink from 'src/components/TextLink';
import { TooltipPerformanceNotAdmin } from 'src/components/TooltipPerformanceNotAdmin';
import { AccountsTable } from './components/AccountsTable';
import { useQueryChannelAccounts } from './useQueryChannelAccounts';
import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';

export const AdAccountsSelectionPopup = ({ open, channel, onSubmit, onClose }) => {
  const styles = useStyles();

  const isAdmin = useIsCurrentUserAdmin();
  const { accounts, selectedIds, loadingAccounts, accountsSortField, accountsSortDirection } =
    useViewState(channel);

  const [selectedAccountIds, setSelectedAccountIds] = useState(selectedIds);

  const { handleSortChange } = useHandlers(channel);

  useQueryChannelAccounts(channel);

  const handleAccountClick = useCallback(
    (account) => {
      const newList = selectedAccountIds.includes(account.id)
        ? without(selectedAccountIds, account.id)
        : [...selectedAccountIds, account.id];

      setSelectedAccountIds(newList);
    },
    [selectedAccountIds, setSelectedAccountIds]
  );

  const handleConfirm = useCallback(() => {
    onSubmit({ channel, selectedAccountIds });
  }, [onSubmit, channel, selectedAccountIds]);

  const allAccountsSelected = selectedAccountIds.length === accounts.length;

  const handleSelectAll = useCallback(() => {
    if (allAccountsSelected) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(accounts.map((account) => account.id));
    }
  }, [allAccountsSelected, setSelectedAccountIds, accounts]);

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={onClose}
      maxWidth="md"
      data-testid="AdAccountsSelectionPopup"
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.popupTitle}>
          <ChannelIcon
            className={styles.channelIcon}
            channel={channel}
            meta
          />
          {getChannelPerformanceLabel(channel)}
        </Typography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {loadingAccounts ? (
          <CircularProgress className="block mx-auto" />
        ) : accounts?.length === 0 ? (
          <div className={styles.emptyWrapper}>
            <Typography className={styles.textEmpty}>
              Couldn&apos;t find any ad accounts.
              <br />
              <br />
              Please make sure your {getChannelPerformanceShortLabel(channel)} Ad profile is
              associated with an ad account.
            </Typography>
          </div>
        ) : (
          <>
            <div className={styles.topWrapper}>
              <div>
                <Typography className={styles.title}>Connect Accounts</Typography>
                <Typography className={styles.description}>
                  Turn on/off the accounts you want Anyword to connect
                </Typography>
                <TextLink
                  className={styles.selectAll}
                  onClick={handleSelectAll}
                  component="button"
                >
                  Turn {allAccountsSelected ? 'off' : 'on'} all accounts
                </TextLink>
              </div>
            </div>
            <AccountsTable
              accounts={accounts}
              connectedAccountIds={selectedIds}
              selectedAccountIds={selectedAccountIds}
              onAccountClick={handleAccountClick}
              onSortChange={handleSortChange}
              sortField={accountsSortField}
              sortDirection={accountsSortDirection}
              channel={channel}
            />
          </>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button
          className={styles.buttonCancel}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="AddAccountSelectionPopup__CancelButton"
        >
          Cancel
        </Button>
        <TooltipPerformanceNotAdmin isAdmin={isAdmin}>
          <Button
            className={styles.buttonConfirm}
            onClick={handleConfirm}
            disabled={!isAdmin}
            variant="contained"
            color="primary"
            aria-label="Confirm"
            data-testid="AddAccountSelectionPopup__CTAButton"
          >
            Apply
          </Button>
        </TooltipPerformanceNotAdmin>
      </DialogActions>
    </Dialog>
  );
};

AdAccountsSelectionPopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
AdAccountsSelectionPopup.defaultProps = {
  className: null,
  open: false,
};
